import { type RefObject, useEffect, useState } from 'react';
import { type Tab } from './TabList';

type UseTabEdgeVisibilityParams<T> = {
  tabs: Tab<T>[];
  tabsContainerRef: RefObject<HTMLUListElement | null>;
  firstTabRef: RefObject<HTMLLIElement | null>;
  lastTabRef: RefObject<HTMLLIElement | null>;
};

type TabEdgeVisibility = {
  isFirstTabVisible: boolean;
  isLastTabVisible: boolean;
  leftGradientOffset: number;
};

export function useTabEdgeVisibility<T>({
  tabs,
  tabsContainerRef,
  firstTabRef,
  lastTabRef,
}: UseTabEdgeVisibilityParams<T>): TabEdgeVisibility {
  const [isFirstTabVisible, setIsFirstTabVisible] = useState(false);
  const [isLastTabVisible, setIsLastTabVisible] = useState(false);
  const [leftGradientOffset, setLeftGradientOffset] = useState(0);

  useEffect(() => {
    function checkVisibility() {
      if (!tabsContainerRef.current || !firstTabRef.current) return;

      setLeftGradientOffset(tabsContainerRef.current.offsetLeft);

      const containerRect = tabsContainerRef.current.getBoundingClientRect();
      const firstTabRect = firstTabRef.current.getBoundingClientRect();

      setIsFirstTabVisible(firstTabRect.left >= containerRect.left && firstTabRect.right <= containerRect.right);

      if (lastTabRef.current) {
        const lastTabRect = lastTabRef.current.getBoundingClientRect();
        setIsLastTabVisible(lastTabRect.left >= containerRect.left && lastTabRect.right <= containerRect.right);
      } else {
        setIsLastTabVisible(true);
      }
    }

    checkVisibility();

    let rafId = 0;
    function scheduleVisibilityCheck() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(checkVisibility);
    }

    const tabsContainer = tabsContainerRef.current;

    window.addEventListener('resize', scheduleVisibilityCheck);
    tabsContainer?.addEventListener('scroll', scheduleVisibilityCheck);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleVisibilityCheck);
      tabsContainer?.removeEventListener('scroll', scheduleVisibilityCheck);
    };
  }, [tabs.length, tabsContainerRef, firstTabRef, lastTabRef]);

  return { isFirstTabVisible, isLastTabVisible, leftGradientOffset };
}
